import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { supabase } from '@/lib/customSupabaseClient';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  BarChart,
  Bar,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Coins,
  Users,
  TrendingUp,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Settings2,
  Infinity,
  Search,
  DollarSign,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { SendEmailButton } from '@/components/email/SendEmailButton';

function formatTokens(n) {
  if (n == null) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function formatCost(usd) {
  if (usd == null || usd === 0) return '$0.00';
  if (usd < 0.01) return '< $0.01';
  return `$${Number(usd).toFixed(2)}`;
}

function monthLabel(offset) {
  const d = new Date();
  d.setMonth(d.getMonth() + offset);
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

const StatsSkeleton = () => (
  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
    {[...Array(4)].map((_, i) => (
      <Card key={i}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-4 rounded" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-7 w-16" />
        </CardContent>
      </Card>
    ))}
  </div>
);

const TableSkeleton = () => (
  <div className="space-y-3">
    {[...Array(5)].map((_, i) => (
      <div key={i} className="flex items-center gap-4 px-4 py-3">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-2 w-32" />
      </div>
    ))}
  </div>
);

const BudgetDialog = ({ user, open, onOpenChange, onSave }) => {
  const [inputLimit, setInputLimit] = useState('');
  const [outputLimit, setOutputLimit] = useState('');
  const [isUnlimited, setIsUnlimited] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setInputLimit(String(user.monthly_input_limit || 2500000));
      setOutputLimit(String(user.monthly_output_limit || 150000));
      setIsUnlimited(user.is_unlimited || false);
    }
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase.rpc('admin_set_token_budget', {
        p_user_id: user.user_id,
        p_monthly_input_limit: parseInt(inputLimit) || 2500000,
        p_monthly_output_limit: parseInt(outputLimit) || 150000,
        p_is_unlimited: isUnlimited,
      });
      if (error) throw error;
      toast.success(`Budget updated for ${user.full_name || user.email}`);
      onSave();
      onOpenChange(false);
    } catch (err) {
      toast.error(`Failed to update budget: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Token Budget</DialogTitle>
          <DialogDescription>
            {user.full_name || user.email}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="unlimited">Unlimited</Label>
            <Switch
              id="unlimited"
              checked={isUnlimited}
              onCheckedChange={setIsUnlimited}
            />
          </div>
          {!isUnlimited && (
            <>
              <div className="space-y-2">
                <Label htmlFor="input-limit">Monthly Input Token Limit</Label>
                <Input
                  id="input-limit"
                  type="number"
                  value={inputLimit}
                  onChange={(e) => setInputLimit(e.target.value)}
                  placeholder="2500000"
                />
                <p className="text-xs text-muted-foreground">
                  Current: {formatTokens(parseInt(inputLimit))} tokens
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="output-limit">Monthly Output Token Limit</Label>
                <Input
                  id="output-limit"
                  type="number"
                  value={outputLimit}
                  onChange={(e) => setOutputLimit(e.target.value)}
                  placeholder="150000"
                />
                <p className="text-xs text-muted-foreground">
                  Current: {formatTokens(parseInt(outputLimit))} tokens
                </p>
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Budget'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TokenUsage = () => {
  const [monthOffset, setMonthOffset] = useState(0);
  const [usageData, setUsageData] = useState([]);
  const [dailyData, setDailyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingDaily, setLoadingDaily] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [daysBack, setDaysBack] = useState('30');
  const [editUser, setEditUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchUsage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: rpcError } = await supabase.rpc('admin_get_token_usage', {
        p_month_offset: monthOffset,
      });
      if (rpcError) throw rpcError;
      setUsageData(data || []);
    } catch (err) {
      console.error('Error fetching token usage:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [monthOffset]);

  const fetchDaily = useCallback(async () => {
    setLoadingDaily(true);
    try {
      const { data, error: rpcError } = await supabase.rpc('admin_get_token_usage_daily', {
        p_days_back: parseInt(daysBack),
      });
      if (rpcError) throw rpcError;
      setDailyData(
        (data || []).map((d) => ({
          ...d,
          day: new Date(d.day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          total_tokens: (d.input_tokens || 0) + (d.output_tokens || 0),
        }))
      );
    } catch (err) {
      console.error('Error fetching daily usage:', err);
    } finally {
      setLoadingDaily(false);
    }
  }, [daysBack]);

  useEffect(() => { fetchUsage(); }, [fetchUsage]);
  useEffect(() => { fetchDaily(); }, [fetchDaily]);

  const stats = useMemo(() => {
    if (!usageData.length) return { totalInput: 0, totalOutput: 0, totalCost: 0, activeUsers: 0, overBudget: 0 };
    const totalInput = usageData.reduce((s, r) => s + (r.input_tokens_used || 0), 0);
    const totalOutput = usageData.reduce((s, r) => s + (r.output_tokens_used || 0), 0);
    const totalCost = usageData.reduce((s, r) => s + Number(r.estimated_cost_usd || 0), 0);
    const overBudget = usageData.filter(
      (r) => !r.is_unlimited && (r.input_percent_used >= 100 || r.output_percent_used >= 100)
    ).length;
    return { totalInput, totalOutput, totalCost, activeUsers: usageData.length, overBudget };
  }, [usageData]);

  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return usageData;
    const term = searchTerm.toLowerCase();
    return usageData.filter(
      (r) =>
        (r.full_name || '').toLowerCase().includes(term) ||
        (r.email || '').toLowerCase().includes(term)
    );
  }, [usageData, searchTerm]);

  const handleEditBudget = (user) => {
    setEditUser(user);
    setDialogOpen(true);
  };

  return (
    <>
      <Helmet>
        <title>Token Usage - My Movement Space</title>
      </Helmet>

      <div className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight">Token Usage</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Monitor and control LLM token spend across all users.
        </p>
      </div>

      <div className="space-y-6">
        {/* Overview Stats */}
        {loading ? (
          <StatsSkeleton />
        ) : error ? (
          <Card>
            <CardContent className="flex items-center justify-center h-24 text-sm text-destructive">
              {error}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Input Tokens</CardTitle>
                <Coins className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatTokens(stats.totalInput)}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {monthLabel(monthOffset)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Output Tokens</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatTokens(stats.totalOutput)}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {monthLabel(monthOffset)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Estimated Cost</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCost(stats.totalCost)}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Haiku 4.5 rates
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active Users</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.activeUsers}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Over Budget</CardTitle>
                <AlertTriangle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className={cn('text-2xl font-bold', stats.overBudget > 0 && 'text-destructive')}>
                  {stats.overBudget}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Daily Usage Chart */}
        <Card>
          <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle>Daily Token Usage</CardTitle>
              <CardDescription>Input and output tokens per day</CardDescription>
            </div>
            <Select value={daysBack} onValueChange={setDaysBack}>
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 Days</SelectItem>
                <SelectItem value="30">Last 30 Days</SelectItem>
                <SelectItem value="90">Last 90 Days</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            {loadingDaily ? (
              <Skeleton className="h-[250px] w-full" />
            ) : dailyData.length === 0 ? (
              <div className="flex items-center justify-center h-[250px] text-sm text-muted-foreground">
                No usage data for this period.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={dailyData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="inputGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="outputGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--chart-2, 250 80% 60%))" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(var(--chart-2, 250 80% 60%))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="day" tick={{ fontSize: 12 }} className="text-muted-foreground" />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={formatTokens} className="text-muted-foreground" />
                    <RechartsTooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--popover))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                      fontSize: '12px',
                    }}
                    formatter={(value, name) => {
                      if (name === 'estimated_cost_usd') return [formatCost(value), 'Est. Cost'];
                      return [formatTokens(value), name === 'input_tokens' ? 'Input' : 'Output'];
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="input_tokens"
                    stroke="hsl(var(--primary))"
                    fill="url(#inputGrad)"
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="output_tokens"
                    stroke="hsl(var(--chart-2, 250 80% 60%))"
                    fill="url(#outputGrad)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Per-User Table */}
        <Card>
          <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setMonthOffset((p) => p - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <CardTitle className="text-base">{monthLabel(monthOffset)}</CardTitle>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={monthOffset >= 0}
                onClick={() => setMonthOffset((p) => p + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                className="pl-9"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <TableSkeleton />
            ) : filteredData.length === 0 ? (
              <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
                {searchTerm ? 'No users match your search.' : 'No token usage this month.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead className="text-right">Input</TableHead>
                      <TableHead className="text-right">Output</TableHead>
                      <TableHead className="text-right">Messages</TableHead>
                      <TableHead className="text-right">Est. Cost</TableHead>
                      <TableHead>Budget Usage</TableHead>
                      <TableHead className="w-10" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredData.map((row) => {
                      const maxPercent = Math.max(
                        row.input_percent_used || 0,
                        row.output_percent_used || 0
                      );
                      return (
                        <TableRow key={row.user_id}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-sm truncate max-w-[200px]">
                                {row.full_name || 'Unknown'}
                              </p>
                              <div className="flex items-center gap-1">
                                <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                                  {row.email}
                                </p>
                                <SendEmailButton userId={row.user_id} fullName={row.full_name} email={row.email} />
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm">
                            {formatTokens(row.input_tokens_used)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm">
                            {formatTokens(row.output_tokens_used)}
                          </TableCell>
                          <TableCell className="text-right text-sm">
                            {row.message_count}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm">
                            {formatCost(row.estimated_cost_usd)}
                          </TableCell>
                          <TableCell>
                            {row.is_unlimited ? (
                              <Badge variant="secondary" className="gap-1">
                                <Infinity className="h-3 w-3" />
                                Unlimited
                              </Badge>
                            ) : (
                              <div className="flex items-center gap-2 min-w-[140px]">
                                <Progress
                                  value={Math.min(maxPercent, 100)}
                                  className={cn(
                                    'h-2 flex-1',
                                    maxPercent >= 100 && '[&>div]:bg-destructive',
                                    maxPercent >= 80 && maxPercent < 100 && '[&>div]:bg-yellow-500'
                                  )}
                                />
                                <span
                                  className={cn(
                                    'text-xs font-medium w-10 text-right',
                                    maxPercent >= 100 && 'text-destructive',
                                    maxPercent >= 80 && maxPercent < 100 && 'text-yellow-600'
                                  )}
                                >
                                  {maxPercent.toFixed(0)}%
                                </span>
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleEditBudget(row)}
                            >
                              <Settings2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <BudgetDialog
        user={editUser}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSave={fetchUsage}
      />
    </>
  );
};

export default TokenUsage;
